import {
  Badge,
  Button,
  DestructiveConfirm,
  Form,
  FormControl,
  FormDrawer,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MobileListCard,
  Spinner,
  Textarea,
  toast,
  VersionBumpSelector,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { FlaskConical, Globe, Tag, Trash2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import {
  useCreateSkillRelease,
  useDeleteSkillRelease,
  useDeprecateSkillRelease,
  usePublishSkillRelease,
  useSkillReleases,
} from '../hooks/use-skills';
import type { SkillRelease, SkillReleaseStatus } from '../types';

// ─── Version helpers (mirrors NewReleasePage logic) ───────────────────────────

type BumpType = 'major' | 'minor' | 'patch';

function parseVersion(v: string): [number, number, number] {
  const m = v.match(/v?(\d+)\.(\d+)\.(\d+)/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [1, 0, 0];
}

function bumpVersion(base: [number, number, number], bump: BumpType): [number, number, number] {
  const [maj, min, pat] = base;
  if (bump === 'major') return [maj + 1, 0, 0];
  if (bump === 'minor') return [maj, min + 1, 0];
  return [maj, min, pat + 1];
}

function parseBetaNumber(name: string): { base: [number, number, number]; n: number } | null {
  const m = name.match(/v?(\d+)\.(\d+)\.(\d+)-beta\.(\d+)$/);
  if (!m) return null;
  return { base: [Number(m[1]), Number(m[2]), Number(m[3])], n: Number(m[4]) };
}

function isBetaContinuation(
  betaBase: [number, number, number],
  bump: BumpType,
  currentBase: [number, number, number],
): boolean {
  const next = bumpVersion(currentBase, bump);
  return next[0] === betaBase[0] && next[1] === betaBase[1] && next[2] === betaBase[2];
}

function nextVersionName(
  currentVersion: string,
  bump: BumpType,
  beta: boolean,
  latestReleaseName: string,
): string {
  const base = parseVersion(currentVersion);
  const betaParsed = parseBetaNumber(latestReleaseName);
  if (beta && betaParsed && isBetaContinuation(betaParsed.base, bump, base)) {
    const [maj, min, pat] = betaParsed.base;
    return `v${maj}.${min}.${pat}-beta.${betaParsed.n + 1}`;
  }
  const [maj, min, pat] = bumpVersion(base, bump);
  return beta ? `v${maj}.${min}.${pat}-beta.1` : `v${maj}.${min}.${pat}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusVariant(status: SkillReleaseStatus): 'success' | 'warning' | 'secondary' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'DRAFT') return 'warning';
  return 'secondary';
}

// ─── Create release form ──────────────────────────────────────────────────────

const BUMP_OPTIONS = [
  { value: 'patch', label: 'Patch', description: 'Bug fixes and minor corrections' },
  { value: 'minor', label: 'Minor', description: 'New features, backwards-compatible' },
  { value: 'major', label: 'Major', description: 'Breaking changes or major reworks' },
] as const;

const createSchema = z.object({
  bump: z.enum(['major', 'minor', 'patch']),
  beta: z.boolean(),
  notes: z.string().optional(),
});

type CreateValues = z.infer<typeof createSchema>;

function CreateReleaseDrawer({
  open,
  onOpenChange,
  appSlug,
  skillName,
  skillVersion,
  latestReleaseName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  appSlug: string;
  skillName: string;
  skillVersion: string;
  latestReleaseName: string;
}) {
  const createMutation = useCreateSkillRelease(appSlug, skillName);
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { bump: 'patch', beta: false, notes: '' },
  });

  const watchBump = form.watch('bump');
  const watchBeta = form.watch('beta');

  const bumpOptions = useMemo(
    () =>
      BUMP_OPTIONS.map((o) => ({
        ...o,
        preview: nextVersionName(skillVersion, o.value as BumpType, watchBeta, latestReleaseName),
      })),
    [skillVersion, watchBeta, latestReleaseName],
  );

  const previewName = nextVersionName(skillVersion, watchBump, watchBeta, latestReleaseName);

  async function onSubmit(data: CreateValues) {
    try {
      const name = nextVersionName(skillVersion, data.bump, data.beta, latestReleaseName);
      await createMutation.mutateAsync({ name, notes: data.notes || undefined });
      form.reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create release');
    }
  }

  return (
    <FormDrawer
      open={open}
      onOpenChange={(v) => {
        form.reset();
        onOpenChange(v);
      }}
      title={`New Release · ${previewName}`}
      onSubmit={() => void form.handleSubmit(onSubmit)()}
      isPending={createMutation.isPending}
      isDirty={form.formState.isDirty}
      submitLabel="Create"
    >
      <Form {...form}>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Version</p>
            <FormField
              control={form.control}
              name="bump"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <VersionBumpSelector
                      options={bumpOptions}
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as BumpType)}
                      beta={watchBeta}
                      onBetaChange={(v) => form.setValue('beta', v)}
                      betaLabel="Beta release"
                      betaDescription={
                        <>
                          Appends <code className="font-mono text-xs">-beta.1</code> to the version
                        </>
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder="What changed in this release…"
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </FormDrawer>
  );
}

// ─── Release card ─────────────────────────────────────────────────────────────

function ReleaseCard({
  release,
  onPublish,
  onDeprecate,
  onDelete,
  onTest,
  onNavigate,
}: {
  release: SkillRelease;
  onPublish: (id: string, name: string) => void;
  onDeprecate: (id: string, name: string) => void;
  onDelete: (release: SkillRelease) => void;
  onTest: () => void;
  onNavigate: () => void;
}) {
  const canPublish = release.status === 'DRAFT';
  const canDeprecate = release.status === 'PUBLISHED';
  const canDelete = release.status === 'DRAFT';

  // Right swipe: Publish OR Deprecate, then Test
  const rightActions = [
    ...(canPublish
      ? [
          {
            label: 'Publish',
            icon: <Globe className="size-4" />,
            onClick: () => onPublish(release.id, release.name),
          },
        ]
      : canDeprecate
        ? [
            {
              label: 'Deprecate',
              icon: <XCircle className="size-4" />,
              onClick: () => onDeprecate(release.id, release.name),
              className: 'bg-destructive',
            },
          ]
        : []),
    {
      label: 'Test',
      icon: <FlaskConical className="size-4" />,
      onClick: onTest,
      className: 'bg-sky-500',
    },
  ];

  // Left swipe: Delete (DRAFT only)
  const leftActions = canDelete
    ? [
        {
          label: 'Delete',
          icon: <Trash2 className="size-4" />,
          onClick: () => onDelete(release),
          className: 'bg-destructive',
        },
      ]
    : [];

  return (
    <MobileListCard
      onClick={onNavigate}
      aria-label={release.name}
      desktopActionsMode="menu"
      swipeActions={rightActions}
      swipeRightActions={leftActions.length > 0 ? leftActions : undefined}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Tag className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-mono text-sm font-medium">{release.name}</p>
            <Badge variant={statusVariant(release.status)} appearance="soft" size="sm">
              {release.status}
            </Badge>
          </div>
          {release.notes && (
            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{release.notes}</p>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">
            {new Date(release.createdAt).toLocaleDateString()}
            {release.createdBy ? ` · ${release.createdBy}` : ''}
          </p>
        </div>
      </div>
    </MobileListCard>
  );
}

// ─── SkillReleasesTab ─────────────────────────────────────────────────────────

export function SkillReleasesTab({
  appSlug,
  skillName,
  skillVersion,
}: {
  appSlug: string;
  skillName: string;
  skillVersion: string;
}) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SkillRelease | null>(null);

  const { data: releases = [], isLoading } = useSkillReleases(appSlug, skillName);
  const latestReleaseName = releases[0]?.name ?? skillVersion;
  const publishMutation = usePublishSkillRelease(appSlug, skillName);
  const deprecateMutation = useDeprecateSkillRelease(appSlug, skillName);
  const deleteMutation = useDeleteSkillRelease(appSlug, skillName);

  async function handlePublish(releaseId: string, name: string) {
    try {
      await publishMutation.mutateAsync(releaseId);
      toast.success(`${name} published`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to publish');
    }
  }

  async function handleDeprecate(releaseId: string, name: string) {
    try {
      await deprecateMutation.mutateAsync(releaseId);
      toast.success(`${name} deprecated`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to deprecate');
    }
  }

  function handleTest() {
    setSearchParams({ tab: 'preview' }, { replace: true });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {releases.length === 0
            ? 'No releases yet'
            : `${releases.length} release${releases.length !== 1 ? 's' : ''}`}
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          New Release
        </Button>
      </div>

      {releases.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-muted">
            <Tag className="size-5 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">No releases</p>
            <p className="text-xs text-muted-foreground max-w-xs">
              Create a release to snapshot the current skill state and publish it for use.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {releases.map((release) => (
            <ReleaseCard
              key={release.id}
              release={release}
              onPublish={handlePublish}
              onDeprecate={handleDeprecate}
              onDelete={setDeleteTarget}
              onTest={handleTest}
              onNavigate={() => navigate(`/skills/${appSlug}/${skillName}/releases/${release.id}`)}
            />
          ))}
        </div>
      )}

      <CreateReleaseDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        appSlug={appSlug}
        skillName={skillName}
        skillVersion={skillVersion}
        latestReleaseName={latestReleaseName}
      />

      <DestructiveConfirm
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
        title="Delete Release"
        description={
          <>
            Delete release{' '}
            <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? Only DRAFT
            releases can be deleted.
          </>
        }
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }
        }}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
